import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/events/josh-baker-youme-amnesia-ibiza-2-july-2026";
const IMAGE_URL = "https://www.hey-lets-travel.com/assets/gdb/zzz11111111111.png";
const TICKET_URL = "https://skiddle.com/deeplink?type=event&ilid=42277102&skrep=10596638";

const KEY_DATES = [
  "2 July - Opening Party with Josh Baker B2B Prospa, Cloonee, jWave B2B Job de Jong and Yass & Mali",
  "9 July - Jamie Jones, Marsolo, Laidlaw, Natalia Roth and Local Dub",
  "23 July - Josh Baker, Kolter, Sidney Charles and jWave",
  "13 August - Josh Baker B2B Luciano, Marco Faraone, Calvin Clarke and Mike Morrisey",
  "27 August - Eliza Rose B2B Olive F, Luuk van Dijk and Jentzen",
  "1 October - Closing Party, with details still to be announced",
];

const FAQS = [
  {
    question: "When is Josh Baker presents You&Me at Amnesia Ibiza?",
    answer: "Josh Baker presents You&Me runs every Thursday at Amnesia Ibiza from 2 July to 1 October 2026.",
  },
  {
    question: "When is the opening party?",
    answer: "The opening party takes place on Thursday 2 July 2026 at Amnesia Ibiza.",
  },
  {
    question: "Who is playing at the Josh Baker You&Me opening party?",
    answer: "The opening party lineup includes Josh Baker B2B Prospa, Cloonee, jWave B2B Job de Jong and Yass & Mali.",
  },
  {
    question: "Where is Josh Baker You&Me Ibiza taking place?",
    answer: "The residency takes place at Amnesia Ibiza, one of the island's most iconic clubs.",
  },
  {
    question: "Can I book hotels and concierge for the event?",
    answer: "Yes. Through HLT, you can book event tickets, search Ibiza hotels, arrange transfers and join the Ibiza concierge list for help planning the full trip.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Josh Baker Presents You&Me at Amnesia Ibiza 2026: The 2 July Opening Party You Need on Your Ibiza Calendar",
  description:
    "Book Josh Baker presents You&Me at Amnesia Ibiza on Thursday 2 July 2026. Opening party lineup includes Josh Baker B2B Prospa, Cloonee, jWave B2B Job de Jong and Yass & Mali. Plan Ibiza tickets, hotels and concierge with HLT.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-05-25",
  dateModified: "2026-05-25",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "Book Josh Baker You&Me Opening Party Tickets" }) {
  return (
    <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function JoshBakerYouMeArticle() {
  return (
    <>
      <Helmet>
        <title>Josh Baker You&Me Amnesia Ibiza 2026 Tickets | 2 July Opening Party</title>
        <meta
          name="description"
          content="Book Josh Baker presents You&Me at Amnesia Ibiza on Thursday 2 July 2026. Opening party lineup includes Josh Baker B2B Prospa, Cloonee, jWave B2B Job de Jong and Yass & Mali. Plan Ibiza tickets, hotels and concierge with HLT."
        />
        <meta
          name="keywords"
          content="Josh Baker Ibiza 2026, Josh Baker You&Me Amnesia, You&Me Ibiza tickets, Josh Baker Prospa Ibiza, Amnesia Ibiza 2 July 2026, Cloonee Amnesia Ibiza, Ibiza house music events 2026, Ibiza party holiday 2026, Josh Baker Amnesia tickets, HLT Ibiza concierge, Ibiza hotel and ticket packages, Ibiza events July 2026"
        />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Josh Baker You&Me Amnesia Ibiza 2026 Tickets | 2 July Opening Party" />
        <meta property="og:description" content="Plan the Josh Baker presents You&Me opening party at Amnesia Ibiza with HLT tickets, hotels and concierge support." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Josh Baker You&Me Amnesia Ibiza 2026 Tickets" />
        <meta name="twitter:description" content="Book the 2 July 2026 You&Me opening party at Amnesia Ibiza and build your Ibiza trip with HLT." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">Ibiza 2026 guide</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              Josh Baker You&Me at Amnesia Ibiza 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              The Thursday 2 July opening party brings Josh Baker B2B Prospa, Cloonee, jWave B2B Job de Jong and Yass & Mali to Amnesia Ibiza for one of the standout house music dates of summer 2026.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta />
              <Link to="/concierge" className="btn-secondary">Join the HLT Ibiza Concierge</Link>
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Venue</dt><dd>Amnesia Ibiza</dd></div>
              <div><dt className="text-white">Room</dt><dd>The Terraza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Thursday 2 July 2026</dd></div>
              <div><dt className="text-white">Season</dt><dd>Every Thursday from 2 July to 1 October 2026</dd></div>
              <div><dt className="text-white">Music</dt><dd>Underground house, minimal, tech house and groove-led electronic music</dd></div>
              <div><dt className="text-white">Age</dt><dd>18+</dd></div>
            </dl>
          </aside>
        </header>

        <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="group block overflow-hidden border border-brand-500/25 bg-black shadow-[0_24px_90px_rgba(0,0,0,0.55)] transition hover:border-brand-500/70">
          <img src="/assets/gdb/zzz11111111111.png" alt="Josh Baker and Prospa You&Me at Amnesia Ibiza event poster" className="block h-auto w-full transition duration-500 group-hover:scale-[1.01]" />
        </a>

        <div className="mx-auto max-w-4xl space-y-10">
          <section className="space-y-4 text-base leading-8 text-slate-300">
            <p>
              Ibiza 2026 is already shaping up to be one of the biggest summers for house music in years. Amnesia is celebrating its 50th anniversary season, the island's event calendar is filling fast, and thousands of house music fans are already choosing the parties they want to build their trips around.
            </p>
            <p>
              One date stands out immediately: Thursday 2 July 2026. That is when Josh Baker presents You&Me officially lands at Amnesia Ibiza for the opening party of his first full Ibiza residency.
            </p>
            <p>
              Running every Thursday from 2 July to 1 October 2026, the residency brings underground house, minimal, groove-led energy and carefully curated lineups to one of the most iconic dancefloors on the island.
            </p>
            <TicketCta />
          </section>

          <ArticleSection title="Why the 2 July opening party is so big">
            <p>
              The opening night on Thursday 2 July 2026 is the launch of Josh Baker's You&Me residency at Amnesia, and the lineup makes it one of the strongest early-July events in Ibiza.
            </p>
            <p>
              The headline moment is Josh Baker B2B Prospa. Josh Baker has become one of the most exciting names in the current UK house scene, building You&Me into a movement known for proper dancefloor energy, underground lineups and a loyal community of house music fans.
            </p>
            <p>
              Prospa bring a different kind of intensity: euphoric, high-energy, UK-rooted electronic music built for big rooms and peak moments. Together on the Amnesia Terraza, this back-to-back set has all the ingredients of a serious Ibiza moment.
            </p>
            <ul className="grid gap-2 border-l border-brand-500/40 pl-5 text-white">
              <li>Josh Baker B2B Prospa</li>
              <li>Cloonee</li>
              <li>jWave B2B Job de Jong</li>
              <li>Yass & Mali</li>
            </ul>
          </ArticleSection>

          <ArticleSection title="The You&Me Amnesia Ibiza 2026 residency">
            <p>
              The You&Me residency runs every Thursday from 2 July to 1 October 2026. Across the season, Josh Baker is joined by a strong mix of established names, underground selectors and rising artists who fit the You&Me sound.
            </p>
            <div className="grid gap-3">
              {KEY_DATES.map((date) => (
                <div key={date} className="border border-white/10 bg-white/[0.03] p-4 text-slate-200">{date}</div>
              ))}
            </div>
            <p>
              Other artists appearing across the residency include Enzo Siragusa, Kepler, L.P. Rhythm, Alexandria, Christian AB, Dr. Banana and Julian Fijma.
            </p>
          </ArticleSection>

          <ArticleSection title="Why house music fans are choosing Ibiza around events like this">
            <p>
              Ibiza holidays are changing. More people are planning trips around specific artists, labels, collectives and events. They are not just arriving on the island and hoping for a good night. They already know the parties they want to attend before they book the hotel.
            </p>
            <p>
              Josh Baker presents You&Me at Amnesia Ibiza sits in the middle of three major trends: house music travel, curated clubbing experiences and premium group travel.
            </p>
            <p>
              HLT is being built for people who travel for music: people who want to experience Ibiza properly, not just book a random package and hope the nightlife works out.
            </p>
          </ArticleSection>

          <ArticleSection title="Plan your Ibiza trip around Josh Baker at Amnesia">
            <p>
              If you are thinking about Ibiza in July 2026, the Josh Baker You&Me opening party on 2 July is one of the best dates to build your trip around.
            </p>
            <p>Through HLT, you can plan:</p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              <li>Event tickets for Josh Baker presents You&Me at Amnesia</li>
              <li>Hotels close to Ibiza nightlife, beach clubs and transport points</li>
              <li>Group villas for friends who want a more private, premium stay</li>
              <li>Airport transfers and club transport</li>
              <li>Concierge support for restaurants, beach clubs, VIP tables and group plans</li>
              <li>Full Ibiza packages combining tickets, accommodation and transfers</li>
            </ul>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="Book Josh Baker You&Me Tickets" />
              <Link to="/concierge" className="btn-secondary">Join the HLT Ibiza Concierge</Link>
              <Link to="/concierge" className="btn-secondary">Find Ibiza Hotels With HLT</Link>
            </div>
          </ArticleSection>

          <ArticleSection title="Who is Josh Baker?">
            <p>
              Josh Baker is a Manchester-born DJ, producer, label founder and one of the most important names in the current UK house music movement.
            </p>
            <p>
              He is the founder of You&Me Records, the creator behind Hide&Seek Festival, and the force behind SYNTHO, a music production mentorship programme helping develop the next generation of artists.
            </p>
            <p>
              His sound connects with a crowd that wants more than commercial club music. It is house music with groove, feeling, movement and identity.
            </p>
          </ArticleSection>

          <ArticleSection title="Why Amnesia Ibiza is the right home for You&Me">
            <p>
              Amnesia is one of Ibiza's most iconic clubs. In 2026, the venue celebrates 50 years of music, dancefloor culture and unforgettable nights on the island.
            </p>
            <p>
              The Amnesia Terraza has history. It is a room associated with serious clubbing, big moments and crowds who know why they are there. Bringing Josh Baker and You&Me into that space every Thursday gives the residency a sense of weight.
            </p>
          </ArticleSection>

          <ArticleSection title="Why book early for 2 July?">
            <p>
              The 2 July opening party is likely to be one of the highest-demand dates of the residency. It is the opening night, the lineup is strong, and early July is a prime time to travel to Ibiza.
            </p>
            <p>
              If you already know you want to be there, the smartest move is to secure your ticket first and build the rest of the trip around it.
            </p>
            <TicketCta children="Secure Your 2 July Opening Party Ticket" />
          </ArticleSection>

          <ArticleSection title="Build the trip with HLT">
            <p>
              HLT is here to make house music travel easier. We help you find the event, secure the ticket and build the trip around it. That could mean a simple ticket purchase, or it could mean a full Ibiza plan with hotels, villas, transfers and concierge support.
            </p>
            <p>
              If you are travelling with friends, celebrating something, or want help creating a smoother Ibiza experience, join the HLT concierge list and we will help you plan around the event.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/concierge" className="btn-primary">Join the HLT Ibiza Concierge</Link>
              <TicketCta children="Book Josh Baker You&Me at Amnesia Ibiza" />
            </div>
          </ArticleSection>

          <section className="border-t border-white/10 pt-8">
            <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
            <div className="mt-5 grid gap-3">
              {FAQS.map((item) => (
                <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                  <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                  <p className="mt-3 leading-7 text-slate-300">{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
